import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Award, Building2, FileText, MessageCircle, Package, Phone, Send, X } from 'lucide-react';
import { translate } from '../i18n';
import { quoteFormspreeUrl } from '../formspree';

const quickReplies = [
  { label: 'Produits et formats', question: 'Quels produits et formats proposez-vous ?', icon: Package },
  { label: 'Qualité et documents', question: 'Quelles certifications et quels documents qualité proposez-vous ?', icon: Award },
  { label: 'Demander un devis', question: 'Je souhaite demander un devis.', icon: FileText },
  { label: 'Coordonnées', question: 'Comment vous contacter ?', icon: Phone }
];

const Chatbot = ({ chatbotOpen, setChatbotOpen, setCurrentPage, usine, language }) => {
  const t = (text) => translate(language, text);
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Bonjour ! Je suis l’assistant de PHOSPHOKYM. Comment puis-je vous aider aujourd’hui ?' }
  ]);
  const [step, setStep] = useState('menu');
  const [contact, setContact] = useState({ nom: '', email: '', tel: '', societe: '' });
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [formError, setFormError] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!chatbotOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setChatbotOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [chatbotOpen, setChatbotOpen]);

  const answerQuestion = (text) => {
    const normalized = text.toLocaleLowerCase('fr').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (/prix|price|devis|quote|tarif|tonnage|volume|commande|order|acheter|buy/.test(normalized)) {
      return {
        text: t('Pour préparer une offre adaptée, précisez votre volume, votre destination et le format souhaité. Notre équipe commerciale pourra étudier votre demande.'),
        action: 'devis',
        actionLabel: t('Accéder au formulaire de devis')
      };
    }
    if (/qualite|quality|certif|iso|document|coa|analyse|traceability|tracabilite/.test(normalized)) {
      return {
        text: t('Phosphokym est certifiée ISO 22000:2018. Les documents qualité et informations disponibles peuvent être demandés à notre équipe selon votre besoin.'),
        action: 'certification',
        actionLabel: t('Voir qualité et traçabilité')
      };
    }
    if (/contact|phone|telephone|email|address|adresse|joindre|coordonne/.test(normalized)) {
      return {
        text: `${t('Téléphone')} : ${usine.telephone}\nEmail : ${usine.email}\n${t('Adresse')} : ${usine.adresse}, ${usine.ville}`,
        action: 'contact',
        actionLabel: t('Ouvrir la page contact')
      };
    }
    if (/format|powder|granul|sac|bag|bulk|vrac|produit|product|phosphate|dcp|feed|aliment/.test(normalized)) {
      return {
        text: t('Notre offre est un DCP Feed Grade granulé, conditionné en big bags pour la nutrition animale.'),
        action: 'produits',
        actionLabel: t('Voir le DCP et ses spécifications')
      };
    }
    if (/usine|phosphokym|entreprise|company|societe|who are/.test(normalized)) {
      return {
        text: t('Phosphokym accompagne les acheteurs professionnels dans leur approvisionnement en phosphate dicalcique pour la nutrition animale et le secteur agricole.'),
        action: 'phosphokym',
        actionLabel: t('En savoir plus sur Phosphokym')
      };
    }
    return {
      text: t('Je peux vous renseigner sur le DCP et ses formats, la qualité et les documents, les devis ou les coordonnées. Choisissez un sujet ci-dessous ou contactez directement notre équipe.')
    };
  };

  const handleQuestion = (text, displayText = text) => {
    if (!text.trim() || isTyping) return;
    setQuestion('');
    setMessages((previous) => [...previous, { sender: 'user', text: displayText }]);
    setIsTyping(true);

    window.setTimeout(() => {
      setMessages((previous) => [...previous, { sender: 'bot', ...answerQuestion(text) }]);
      setIsTyping(false);
    }, 350);
  };

  const handleCommercialRequest = () => {
    setMessages((previous) => [
      ...previous,
      { sender: 'user', text: t('Être contacté par un commercial') },
      { sender: 'bot', text: t('Laissez vos coordonnées professionnelles et notre équipe pourra vous recontacter.') }
    ]);
    setFormError('');
    setStep('form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submissionData = {
      societe: contact.societe,
      nom: contact.nom,
      email: contact.email,
      telephone: contact.tel,
      _subject: `${t('Demande de contact commercial')} - ${contact.societe}`,
      _captcha: false
    };

    try {
      const response = await fetch(quoteFormspreeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi');
      }

      setMessages((previous) => [
        ...previous,
        { sender: 'user', text: `${t('Informations envoyées pour')} ${contact.societe}` },
        { sender: 'bot', text: `${t('Merci')} ${contact.nom} ! ${t('Un commercial de')} ${usine.nom} ${t('vous contactera bientôt à')} ${contact.email}.` }
      ]);
      setContact({ nom: '', email: '', tel: '', societe: '' });
      setFormError('');
      setStep('menu');
    } catch (error) {
      setFormError(`${t('L’envoi a échoué. Réessayez ou contactez-nous à')} ${usine.email}.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Bouton Chatbot */}
      <button
        onClick={() => setChatbotOpen(!chatbotOpen)}
        className="chatbot-btn"
        aria-label={chatbotOpen ? t('Fermer l’assistant') : t('Ouvrir l’assistant Phosphokym')}
        aria-expanded={chatbotOpen}
      >
        {chatbotOpen ? <X size={23} /> : <MessageCircle size={24} />}
      </button>

      {/* Fenêtre Chatbot */}
      {chatbotOpen && (
        <section className="chatbot-window" role="dialog" aria-label={`${t('Assistant')} ${usine.nom}`}>
          <div className="chatbot-header">
            <div className="chatbot-title">
              <span className="chatbot-avatar"><MessageCircle size={17} /></span>
              <span>{t('Assistant')} {usine.nom}<small>{t('Informations DCP et demandes B2B')}</small></span>
            </div>
            <button 
              onClick={() => setChatbotOpen(false)}
              className="chatbot-close"
              aria-label={t('Fermer l’assistant')}
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="chatbot-messages" aria-live="polite" aria-relevant="additions text">
            {messages.map((msg, idx) => (
              <div key={`${msg.sender}-${idx}`} className={`message ${msg.sender}`}>
                <div className="message-content">
                  {t(msg.text)}
                  {msg.action && (
                    <button
                      type="button"
                      className="chatbot-action"
                      onClick={() => {
                        setCurrentPage(msg.action);
                        setChatbotOpen(false);
                      }}
                    >
                      {t(msg.actionLabel)}<ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {isTyping && <div className="message bot"><div className="message-content chatbot-typing" role="status">{t('L’assistant écrit')}<span>...</span></div></div>}
            <div ref={messagesEndRef} />
          </div>

          {step === 'menu' && (
            <div className="chatbot-quick-replies">
              {quickReplies.map(({ label, question: reply, icon: Icon }) => (
                <button key={label} type="button" onClick={() => handleQuestion(t(reply), t(label))} disabled={isTyping}>
                  <Icon size={14} />{t(label)}
                </button>
              ))}
              <button type="button" className="chatbot-commercial" onClick={handleCommercialRequest} disabled={isTyping}>
                <Building2 size={14} />{t('Être contacté par un commercial')}
              </button>
            </div>
          )}

          {step === 'form' && (
            <form onSubmit={handleSubmit} className="chatbot-contact-form">
              <label>{t('Société *')}<input type="text" autoComplete="organization" value={contact.societe} onChange={e => setContact({ ...contact, societe: e.target.value })} required /></label>
              <label>{t('Nom *')}<input type="text" autoComplete="name" value={contact.nom} onChange={e => setContact({ ...contact, nom: e.target.value })} required /></label>
              <label>{t('Email professionnel *')}<input type="email" autoComplete="email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} required /></label>
              <label>{t('Téléphone')}<input type="tel" autoComplete="tel" value={contact.tel} onChange={e => setContact({ ...contact, tel: e.target.value })} /></label>
              {formError && <p className="chatbot-form-error" role="alert">{formError}</p>}
              <div className="chatbot-form-actions">
                <button type="button" className="chatbot-cancel" onClick={() => { setStep('menu'); setFormError(''); }}>{t('Annuler')}</button>
                <button type="submit" className="chatbot-submit" disabled={loading}>
                  <Send size={15} />{loading ? t('Envoi en cours...') : t('Envoyer')}
                </button>
              </div>
            </form>
          )}

          {step === 'menu' && (
            <form className="chatbot-composer" onSubmit={(event) => { event.preventDefault(); handleQuestion(question); }}>
              <input
                type="text"
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                placeholder={t('Écrivez votre question...')}
                aria-label={t('Votre question')}
                disabled={isTyping}
              />
              <button type="submit" aria-label={t('Envoyer la question')} disabled={!question.trim() || isTyping}>
                <Send size={17} />
              </button>
            </form>
          )}
        </section>
      )}
    </>
  );
};

export default Chatbot;